import { animate, style, transition, trigger } from "@angular/animations";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, inject, model, output, signal } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { filter, map, take } from "rxjs";

import { LiveAnnouncer } from "@angular/cdk/a11y";
import { toSignal } from "@angular/core/rxjs-interop";
import { BiTranslateService } from "@globals/bi-translate";
import { CustomAnimations } from "@globals/classes/CustomAnimations";
import { isStringNullOrEmpty } from "@globals/helper-functions";
import { SubscriptionDto } from "@globals/openapi-models/model/subscriptionDto";
import { SubscriberInfo } from "../../shared/SubscriptionModuleBaseSharedService";
import { SubscriptionModuleSharedService } from "../subscription-module-shared.service";

const slideUpAnim = trigger("slideUp", [
  transition(":enter", [style({ opacity: "0", transform: "translateY(10px)" }), animate(".3s ease-in", style({ opacity: "1", transform: "translateY(0px)" }))]),
  transition(":leave", [style({ opacity: "1", transform: "translateY(0px)" }), animate(".3s ease-in", style({ opacity: "0", transform: "translateY(10px)" }))])
]);

export type UnsubscribeEventData = { subscription: SubscriptionDto; targetButton: HTMLButtonElement };

@UntilDestroy()
@Component({
  selector: "subscription-overview",
  templateUrl: "./subscription-overview.component.html",
  styleUrls: ["./subscription-overview.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [CustomAnimations.fadeInDown, slideUpAnim],
  standalone: false
})
export class SubscriptionOverviewComponent implements OnInit {
  public subscriptions: SubscriptionDto[];
  @Input() public mobileEmailName: {
    mobile?: string;
    email?: string;
    name?: string;
  };

  onUnsubscribe = output<UnsubscribeEventData>();

  onSubscribe = output<boolean>();

  /**
   * This is a event that is emitted when the user clicks on the finish button.
   * It does the same thing as when you click on the refresh button,
   * but the reason we have this button is because some users are not sure if they have completed the subscription.
   */
  OnReset = output();

  public isLoading = signal(true);
  public subscriberInfo: SubscriberInfo;
  public supplyNumberSubscribeBtnText = this.translator.instant("iFrame.Subscription.SubscribeBySupplyNr");

  /**
   * The text to show when a new subscription has been added/created.
   **/
  public subscribeConfirmedText = toSignal(
    this.sharedService.moduleConfigModel$.pipe(
      filter(x => x != undefined),
      map(x => x.subscriptionConfirmedText)
    )
  );
  showSubscribeConfirmedText = model(false);

  public canSubscribeViaSupplyNumber = toSignal(
    this.sharedService.moduleConfigModel$.pipe(
      filter(x => x != undefined),
      map(x => x.canSubscribeViaSupplyNumber)
    )
  );

  private readonly announcer = inject(LiveAnnouncer);

  constructor(
    private sharedService: SubscriptionModuleSharedService,
    private translator: BiTranslateService,
    private cd: ChangeDetectorRef
  ) {}

  public ngOnInit() {
    this.subscriberInfo = this.sharedService.getCurrentSubscriberInfo();
    // Go get the list of subscriptions if not already done before
    if (!this.sharedService.subscriptionsInitialized) {
      this.sharedService
        .getSubscriptionListData(this.sharedService.urlParams.onlyBusinessLookup, this.sharedService.urlParams.onlyPrivateLookup, this.sharedService.urlParams.disableLookup)
        .pipe(untilDestroyed(this), take(1))
        .subscribe();
    }
    // Listen for changes to the list of subscriptions in shared state service
    this.sharedService.subscriptions$.pipe(untilDestroyed(this)).subscribe(subs => {
      if (this.sharedService.subscriptionsInitialized) {
        this.subscriptions = subs;
        this.isLoading.set(false);
        this.cd.detectChanges();
      }
    });

    if (this.sharedService.getCachedModuleConfigObject().canSubscribeViaSupplyNumber) {
      const supplyNumberText = this.sharedService.getCachedModuleConfigObject().supplyNumberText;
      if (!isStringNullOrEmpty(supplyNumberText)) {
        this.supplyNumberSubscribeBtnText = this.translator.instant("iFrame.Subscription.SubscribeBy", {
          subscribeBy: supplyNumberText.toLowerCase()
        });
      }
    }

    this.cd.detectChanges();

    this.announcer.announce(this.translator.instant("iFrame.Subscription.OverviewSubtitle"));
  }

  public onUnsubscribeClicked(subscription: SubscriptionDto, target: EventTarget) {
    this.onUnsubscribe.emit({ subscription, targetButton: target as HTMLButtonElement });
  }
}
